import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import { debounce } from 'lodash'
import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import { ApiTax } from '@vend/types'
import { add, multiply } from 'vend-number'
import { AMERICAN_STATES, US_COUNTRY_CODE } from '../../../../vend-ui/constants'
import { useSaleManager } from '../../../../ng-compat/hooks'
import {
  FEATURE_CAPTURE_ADDRESS_SELL_SCREEN,
  FEATURE_ENABLED_AUTOTAX_FOR_DELIVERIES,
  FEATURE_NEW_TAX_EXEMPTION_ENABLED,
} from '../../../../constants/wr-features'
import { DEBOUNCE_OPTIONS } from '../../../../constants/debounce-time'
import { FulfilmentType } from '../../../../models/fulfilment'
import { LineItem, LineItemTaxComponent } from '../../../../models/line-item'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  selectLineItems,
  selectSaleFulfilmentDetail,
  selectAutoTaxCalculating,
  selectAutoTaxLastCalculatedKey,
  selectSaleFinals,
  selectPreAutoTaxFinals,
  selectPreAutoTaxLineItems,
  selectAutoTaxAddressError,
  selectAutoTaxInactiveCategoryLineItemIds,
  setAutoTaxCalculating,
  setAutoTaxLastCalculatedKey,
  setPreAutoTaxFinals,
  setPreAutoTaxLineItems,
  setAutoTaxAddressError,
  setAutoTaxInactiveCategoryLineItemIds,
  updateLineItems,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import {
  AddressErrorsStatus,
  getLineItemFulfillmentAddressDetails,
} from '../../sale-line-item/fulfilment/popover/utils/fulfillmentHelper'
import {
  CalculateTax,
  CalculateTaxErrorTypes,
  CalculateTaxInput,
  CalculateTaxResponse,
  ResponseLineItem,
  TaxAddress,
  TaxLineItem,
} from '../../../../queries/calculate-tax'
import { selectActiveOutletId } from '../../../../state/slices/posState/posStateSlice'
import { store } from '../../../../state/store'
import {
  ProductTaxCategory,
  ProductTaxCategoryResponse,
  ProductTaxCategoryVariables,
} from '../../../../queries/product-tax-category'
import { TaxesStore } from '../../../../factories/database/taxes-store'
import { syncManager } from '../../../../ng-compat/managers'

export type AutoTax = {
  isAutoTaxApplied: boolean
  isLoading: boolean
  hasAddressError: boolean
  inactiveCategoryLineItemIds?: string[]
}

const taxesStore = new TaxesStore()

/**
 * Ensures that tax groups referenced in the CalculateTax response exist in IndexedDB.
 * The CalculateTax mutation may create new tax groups on the server that don't exist locally yet.
 * If any are missing, triggers a sync to fetch them.
 */
const syncTaxGroupsIfMissing = async (
  responseLineItems: ResponseLineItem[]
) => {
  try {
    const groupRateIds = new Set(
      responseLineItems.flatMap(item => item.taxes.map(tax => tax.groupRateId))
    )

    const lookups = await Promise.all(
      [...groupRateIds].map(id => taxesStore.get(id).then(tax => ({ id, tax })))
    )

    const hasMissing = lookups.some(({ tax }) => !tax)

    if (hasMissing) {
      syncManager.sync()
    }
  } catch {
    // Non-critical: sync will eventually pick up new tax groups
  }
}

const mapAddressToTaxAddress = (address: AddressDetails): TaxAddress => ({
  countryCode: address.country_id,
  postCode: address.postcode,
  stateCode: address.state,
  city: address.city,
  street: `${address.address_1} ${address.address_2 || ''}`.trim(),
})

const buildTaxInput = (
  details: ReturnType<typeof selectSaleFulfilmentDetail>,
  items: ReturnType<typeof selectLineItems>,
  outletId: string | undefined,
  taxCodes: Record<string, string> = {}
): CalculateTaxInput | undefined => {
  if (!details?.shippingAddress) {
    return
  }

  if (!outletId) {
    return
  }

  const deliveryLineItems: TaxLineItem[] = items
    .filter(
      item =>
        item.fulfilmentType === FulfilmentType.DELIVERY &&
        !item.customerTaxApplied
    )
    .map(item => ({
      id: item.id || item.localId,
      quantity: item.quantity.toString(),
      taxCode: taxCodes[item.product?.id] ?? '',
      unitPrice: item.basePrice.toString(),
      discount: (item.displayDiscount ?? 0).toString(),
    }))

  if (deliveryLineItems.length === 0) {
    return
  }

  return {
    destinationAddress: mapAddressToTaxAddress(details.shippingAddress),
    shippingAmount: '0',
    lineItems: deliveryLineItems,
    outletId,
  }
}

/**
 * Applies calculated taxes to delivery line items
 */
const applyTaxesToLineItems = (
  taxResponse: ResponseLineItem[],
  lineItems: LineItem[],
  stateName: string
): LineItem[] => {
  if (!taxResponse || taxResponse.length === 0) {
    return []
  }

  const taxUpdates = new Map(taxResponse.map(item => [item.id, item.taxes]))

  const updatedLineItems: LineItem[] = []

  lineItems.forEach(lineItem => {
    const taxes = taxUpdates.get(lineItem.id || lineItem.localId)
    if (
      taxes &&
      taxes.length > 0 &&
      lineItem.fulfilmentType === FulfilmentType.DELIVERY &&
      !lineItem.customerTaxApplied
    ) {
      // Calculate total tax
      const totalTax = taxes.reduce(
        (sum, tax) => add(sum, parseFloat(tax.totalTax)),
        0
      )

      const combinedRate = multiply(
        taxes.reduce((sum, tax) => add(sum, parseFloat(tax.rate)), 0),
        100
      )
      const groupName = `${stateName} sales tax (${combinedRate}%)`

      const groupId = taxes[0].groupRateId

      const taxInfo: ApiTax = {
        id: groupId,
        name: groupName,
        version: 1,
        deleted_at: null,
        is_default: false,
        display_name: groupName,
        rates: taxes.map(tax => ({
          id: tax.rateId,
          name: tax.rateName,
          rate: parseFloat(tax.rate),
          rules: [],
          display_name: tax.rateName,
        })),
      }

      const updatedLineItem: LineItem = {
        ...lineItem,
        adjusted: true,
        tax: totalTax.toString(),
        baseTax: totalTax.toString(),
        taxInfo,
        taxComponents: taxes.map(tax => ({
          rate_id: tax.rateId,
          total_tax: parseFloat(tax.totalTax),
          rate: {
            id: tax.rateId,
            name: tax.rateName,
            rate: parseFloat(tax.rate),
            rules: [],
            display_name: tax.rateName,
          },
          total_tax_after_sale_discount: parseFloat(tax.totalTax),
          base_total_tax: parseFloat(tax.totalTax),
        })),
      }

      updatedLineItems.push(updatedLineItem)
    }
  })

  return updatedLineItems
}

export const useAutoTax = (): AutoTax => {
  const isAutoTaxFeatureEnabled = getFeatureFromCache(
    FEATURE_ENABLED_AUTOTAX_FOR_DELIVERIES
  )
  const isCaptureAddressEnabled = getFeatureFromCache(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )

  const fulfillmentDetail = useAppSelector(selectSaleFulfilmentDetail)
  const lineItems = useAppSelector(selectLineItems)
  const isLoading = useAppSelector(selectAutoTaxCalculating)
  const lastCalculatedKey = useAppSelector(selectAutoTaxLastCalculatedKey)
  const hasAddressError = useAppSelector(selectAutoTaxAddressError)
  const inactiveCategoryLineItemIds = useAppSelector(
    selectAutoTaxInactiveCategoryLineItemIds
  )
  const outletId = useAppSelector(selectActiveOutletId)
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const client = useApolloClient()
  const taxCodeCache = useRef<Record<string, string>>({})

  const isNewTaxExemptionEnabled = getFeatureFromCache(
    FEATURE_NEW_TAX_EXEMPTION_ENABLED
  )

  const fetchProductTaxCodes = useCallback(
    async (deliveryItems: LineItem[]): Promise<Record<string, string>> => {
      if (!isNewTaxExemptionEnabled) {
        return {}
      }

      const uniqueProductIds = [
        ...new Set(
          deliveryItems
            .map(item => item.product?.id)
            .filter((id): id is string => !!id)
        ),
      ]

      const missingIds = uniqueProductIds.filter(
        id => !(id in taxCodeCache.current)
      )

      if (missingIds.length > 0) {
        const results = await Promise.all(
          missingIds.map(productId =>
            // TODO: LRX-35725 Optimise fetching of product tax codes for tax calculation
            client
              .query<ProductTaxCategoryResponse, ProductTaxCategoryVariables>({
                query: ProductTaxCategory,
                variables: { productId },
              })
              .then(res => ({
                productId,
                taxCode: res.data?.productTaxCategory?.externalCode ?? '',
              }))
              .catch(() => ({
                productId,
                taxCode: undefined,
              }))
          )
        )

        results.forEach(({ productId, taxCode }) => {
          if (taxCode !== undefined) {
            taxCodeCache.current[productId] = taxCode
          }
        })
      }

      return { ...taxCodeCache.current }
    },
    [isNewTaxExemptionEnabled, client]
  )

  const isAutoTaxApplied = useMemo(() => {
    if (!isAutoTaxFeatureEnabled || !isCaptureAddressEnabled) {
      return false
    }

    const isDelivery = fulfillmentDetail?.type === FulfilmentType.DELIVERY
    if (!isDelivery) {
      return false
    }

    if (fulfillmentDetail?.shippingAddress?.country_id !== US_COUNTRY_CODE) {
      return false
    }

    const deliveryItems = lineItems.filter(
      item => item.fulfilmentType === FulfilmentType.DELIVERY
    )
    if (deliveryItems.length === 0) {
      return false
    }

    // If customer tax is applied to any delivery item, it means the customer
    // has a specific tax rate which should take precedence over auto-tax
    if (deliveryItems.some(item => item.customerTaxApplied)) {
      return false
    }

    const addressDetails = getLineItemFulfillmentAddressDetails(
      fulfillmentDetail
    )
    const hasValidAddresses =
      addressDetails.error === '' &&
      addressDetails.status ===
        AddressErrorsStatus.CORRECTSHIPPING_CORRECTBILLING

    return hasValidAddresses
  }, [
    isAutoTaxFeatureEnabled,
    isCaptureAddressEnabled,
    fulfillmentDetail,
    lineItems,
  ])

  const saleFinals = useAppSelector(selectSaleFinals)
  const preAutoTaxFinals = useAppSelector(selectPreAutoTaxFinals)
  const preAutoTaxLineItems = useAppSelector(selectPreAutoTaxLineItems)

  /**
   * Restores line items to their pre-auto-tax state (original manual tax or zero)
   * and recalculates sale finals accordingly.
   * Used when auto-tax needs to be rolled back (API error or fulfillment type change).
   */
  const restoreAutoTaxLineItems = useCallback(() => {
    if (!preAutoTaxFinals) {
      return
    }

    const restoredLineItems = lineItems.map(lineItem => {
      const originalTaxInfo = preAutoTaxLineItems[lineItem.localId]
      if (originalTaxInfo) {
        return {
          ...lineItem,
          tax: originalTaxInfo.tax,
          baseTax: originalTaxInfo.baseTax,
          taxInfo: originalTaxInfo.taxInfo,
          taxComponents: originalTaxInfo.taxComponents,
        }
      }
      return lineItem
    })

    const hasChanges = restoredLineItems.some(
      (item, idx) => item !== lineItems[idx]
    )

    if (hasChanges) {
      dispatch(updateLineItems(restoredLineItems))

      saleManager.queueSaleCalculation()
    }
  }, [preAutoTaxFinals, preAutoTaxLineItems, lineItems, saleManager, dispatch])

  const [calculateTax] = useMutation<
    CalculateTaxResponse,
    { input: CalculateTaxInput }
  >(CalculateTax, {
    onCompleted: data => {
      dispatch(setAutoTaxCalculating(false))

      if (data.calculateTax.error) {
        const error = data.calculateTax.error

        if (
          error.type === CalculateTaxErrorTypes.INACTIVE_TAX_CATEGORY &&
          error.lineItemIds
        ) {
          dispatch(setAutoTaxInactiveCategoryLineItemIds(error.lineItemIds))
        } else {
          dispatch(setAutoTaxAddressError(error.type))
          dispatch(setAutoTaxInactiveCategoryLineItemIds(undefined))
        }

        restoreAutoTaxLineItems()
        return
      }

      dispatch(setAutoTaxAddressError(undefined))
      dispatch(setAutoTaxInactiveCategoryLineItemIds(undefined))

      syncTaxGroupsIfMissing(data.calculateTax.lineItems)

      // Read the CURRENT Redux state directly from the store, bypassing React's
      // render cycle. This is critical: if customer tax re-pricing completed while
      // the auto-tax API call was in flight, the closure values (lineItems etc.)
      // would be stale and we'd overwrite customer tax with auto-tax.
      const currentState = store.getState()
      const currentLineItems = selectLineItems(currentState)
      const currentFulfillmentDetail = selectSaleFulfilmentDetail(currentState)

      // If any delivery item now has customer tax applied, discard auto-tax result
      const hasCustomerTax = currentLineItems.some(
        item =>
          item.fulfilmentType === FulfilmentType.DELIVERY &&
          item.customerTaxApplied
      )
      if (hasCustomerTax) {
        return
      }

      // Track that we're using auto-tax (for restoration later)
      if (!preAutoTaxFinals) {
        // Store a flag to indicate auto-tax was applied
        // We don't store the actual finals because they can become stale
        dispatch(setPreAutoTaxFinals(saleFinals))
      }

      const stateCode =
        currentFulfillmentDetail?.shippingAddress?.state?.toUpperCase() || ''
      const stateEntry = AMERICAN_STATES.find(
        (s: { code: string; name: string }) => s.code === stateCode
      )
      const stateName = stateEntry ? stateEntry.name : stateCode

      const updatedLineItems = applyTaxesToLineItems(
        data.calculateTax.lineItems,
        currentLineItems,
        stateName
      )

      if (updatedLineItems.length > 0) {
        // Store original tax info for delivery line items before auto-tax replaces them
        const originalTaxInfo: Record<
          string,
          {
            tax: string
            baseTax: string
            taxInfo?: ApiTax
            taxComponents: LineItemTaxComponent[]
          }
        > = { ...preAutoTaxLineItems }

        updatedLineItems.forEach(updatedItem => {
          const originalItem = currentLineItems.find(
            item => item.localId === updatedItem.localId
          )
          // If we found the original item and haven't stored its tax info yet, save it
          // Note: updatedLineItems only contains delivery items that received auto-tax
          if (originalItem && !originalTaxInfo[originalItem.localId]) {
            originalTaxInfo[originalItem.localId] = {
              tax: String(originalItem.tax),
              baseTax: String(originalItem.baseTax),
              taxInfo: originalItem.taxInfo,
              taxComponents: originalItem.taxComponents || [],
            }
          }
        })

        dispatch(setPreAutoTaxLineItems(originalTaxInfo))
        dispatch(updateLineItems(updatedLineItems))
        saleManager.queueSaleCalculation()
      }
    },
    onError: () => {
      dispatch(setAutoTaxCalculating(false))
      // TODO: Add taxes rates to the indexDB and do offline calculation
      dispatch(setAutoTaxAddressError(CalculateTaxErrorTypes.UNSPECIFIED))
      dispatch(setAutoTaxInactiveCategoryLineItemIds(undefined))
      restoreAutoTaxLineItems()
    },
  })

  // Create a stable key that only changes when actual input changes
  const inputKey = useMemo(() => {
    if (!isAutoTaxApplied) {
      return ''
    }

    const input = buildTaxInput(fulfillmentDetail, lineItems, outletId)
    if (!input) {
      return ''
    }

    return JSON.stringify({
      address: input.destinationAddress,
      itemIds: input.lineItems.map(item => item.id).sort(),
    })
  }, [isAutoTaxApplied, fulfillmentDetail, lineItems, outletId])

  // Debounced function to trigger tax calculation
  const calculateTaxDebounced = useRef<ReturnType<typeof debounce>>()

  // Initialize debounced function once
  if (!calculateTaxDebounced.current) {
    calculateTaxDebounced.current = debounce(
      (key: string, input: CalculateTaxInput, currentLastKey: string) => {
        if (!key || currentLastKey === key) {
          return
        }

        dispatch(setAutoTaxAddressError(undefined))
        dispatch(setAutoTaxLastCalculatedKey(key))
        dispatch(setAutoTaxCalculating(true))
        calculateTax({ variables: { input } })
      },
      DEBOUNCE_OPTIONS.wait,
      { maxWait: DEBOUNCE_OPTIONS.maxWait }
    )
  }

  // Trigger calculation when inputKey changes
  useEffect(() => {
    if (!inputKey) {
      return
    }

    const deliveryItems = lineItems.filter(
      item => item.fulfilmentType === FulfilmentType.DELIVERY
    )

    fetchProductTaxCodes(deliveryItems).then(taxCodes => {
      const input = buildTaxInput(
        fulfillmentDetail,
        lineItems,
        outletId,
        taxCodes
      )
      if (input) {
        calculateTaxDebounced.current?.(inputKey, input, lastCalculatedKey)
      }
    })
    // Only depend on inputKey and lastCalculatedKey
    // fulfillmentDetail, lineItems, and fetchProductTaxCodes are already captured in inputKey
    // dispatch and calculateTax are stable references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey, lastCalculatedKey])

  // Cleanup: cancel any pending debounced calls when component unmounts
  useEffect(() => {
    return () => {
      calculateTaxDebounced.current?.cancel()
    }
  }, [])

  // Restore original taxes when fulfillment type changes away from delivery
  useEffect(() => {
    // Only restore if auto-tax features are enabled, auto-tax is no longer
    // active, and we have stored pre-auto-tax state to restore from
    if (
      !isAutoTaxFeatureEnabled ||
      !isCaptureAddressEnabled ||
      isAutoTaxApplied ||
      !preAutoTaxFinals
    ) {
      return
    }

    // Cancel any pending auto-tax calculations
    calculateTaxDebounced.current?.cancel()
    dispatch(setAutoTaxCalculating(false))

    // Remove auto-tax from line items and restore original tax info
    const restoredLineItems = lineItems.map(lineItem => {
      const originalTaxInfo = preAutoTaxLineItems[lineItem.localId]
      if (originalTaxInfo) {
        return {
          ...lineItem,
          tax: originalTaxInfo.tax,
          baseTax: originalTaxInfo.baseTax,
          taxInfo: originalTaxInfo.taxInfo,
          taxComponents: originalTaxInfo.taxComponents,
        }
      }
      return lineItem
    })

    // Check if any line items changed
    const hasChanges = restoredLineItems.some(
      (item, idx) => item !== lineItems[idx]
    )

    if (hasChanges) {
      dispatch(updateLineItems(restoredLineItems))

      saleManager.queueSaleCalculation()
    }

    // Clear the auto-tax tracking state
    dispatch(setPreAutoTaxFinals(null))
    dispatch(setPreAutoTaxLineItems({}))
    dispatch(setAutoTaxLastCalculatedKey(''))
    dispatch(setAutoTaxAddressError(undefined))
    dispatch(setAutoTaxInactiveCategoryLineItemIds(undefined))
  }, [
    isAutoTaxFeatureEnabled,
    isCaptureAddressEnabled,
    isAutoTaxApplied,
    preAutoTaxFinals,
    preAutoTaxLineItems,
    lineItems,
    saleManager,
    dispatch,
  ])

  return {
    isAutoTaxApplied,
    isLoading,
    hasAddressError: !!hasAddressError,
    inactiveCategoryLineItemIds,
  }
}
